/**
 * CallingHoursTimeline — Horizontal 24h bar visualization
 *
 * Displays a single jurisdiction's calling hour windows as colored segments
 * on a 24-hour timeline bar. Green = INFORMATIONAL, Blue = MARKETING.
 */
import { cn } from '@respectly/ui';
import type { CallingHourWindow } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface CallingHoursTimelineProps {
  windows: CallingHourWindow[];
  jurisdictionName: string;
  compact?: boolean;
}

// ============================================
// Helpers
// ============================================

const HOUR_TICKS = [0, 6, 12, 18, 24];

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatHourLabel(hour: number): string {
  if (hour === 0 || hour === 24) return '12a';
  if (hour === 12) return '12p';
  if (hour < 12) return `${hour}a`;
  return `${hour - 12}p`;
}

function formatTime(hour: number, minute: number): string {
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return minute === 0
    ? `${displayHour}:00 ${period}`
    : `${displayHour}:${String(minute).padStart(2, '0')} ${period}`;
}

function formatDays(daysOfWeek: number[]): string {
  if (daysOfWeek.length === 7) return 'Every day';
  if (
    daysOfWeek.length === 5 &&
    [1, 2, 3, 4, 5].every((d) => daysOfWeek.includes(d))
  ) {
    return 'Mon–Fri';
  }
  if (
    daysOfWeek.length === 2 &&
    daysOfWeek.includes(0) &&
    daysOfWeek.includes(6)
  ) {
    return 'Weekends';
  }
  if (
    daysOfWeek.length === 6 &&
    !daysOfWeek.includes(0)
  ) {
    return 'Mon–Sat';
  }
  return daysOfWeek.map((d) => DAY_LABELS[d]).join(', ');
}

function getSegmentColor(purpose: string | null | undefined): {
  bg: string;
  label: string;
} {
  if (purpose === 'INFORMATIONAL') {
    return {
      bg: 'bg-emerald-500/70 dark:bg-emerald-400/50',
      label: 'Informational',
    };
  }
  return {
    bg: 'bg-blue-500/70 dark:bg-blue-400/50',
    label: 'Marketing',
  };
}

// ============================================
// Component
// ============================================

export function CallingHoursTimeline({
  windows,
  jurisdictionName,
  compact = false,
}: CallingHoursTimelineProps) {
  return (
    <div className={cn('w-full', compact ? 'space-y-1' : 'space-y-2')} aria-label={`Calling hours for ${jurisdictionName}`}>
      {/* Timeline — one row per window so overlapping time ranges never collide */}
      <div className={cn(compact ? 'space-y-0.5' : 'space-y-1')}>
        {windows.map((w) => {
          const startFraction =
            (w.startHour + w.startMinute / 60) / 24;
          const endFraction =
            (w.endHour + w.endMinute / 60) / 24;
          const widthPercent = (endFraction - startFraction) * 100;
          const leftPercent = startFraction * 100;
          const color = getSegmentColor(w.messagePurpose);

          return (
            <div key={w.id} className="flex items-center gap-2">
              {/* Leading day-grouping label distinguishes rows at a glance */}
              <span className="w-16 shrink-0 text-[10px] text-slate-500 dark:text-slate-400 truncate">
                {formatDays(w.daysOfWeek)}
              </span>

              {/* Per-window background track + its single segment */}
              <div
                className={cn(
                  'relative flex-1 rounded-md bg-slate-100 dark:bg-slate-800 overflow-hidden',
                  compact ? 'h-6' : 'h-7'
                )}
              >
                <div
                  className={cn(
                    'absolute top-0 bottom-0 rounded-sm transition-opacity hover:opacity-90',
                    color.bg
                  )}
                  style={{
                    left: `${leftPercent}%`,
                    width: `${widthPercent}%`,
                  }}
                  title={`${w.name}\n${formatTime(w.startHour, w.startMinute)} – ${formatTime(w.endHour, w.endMinute)}\n${w.channel ?? 'All'} · ${color.label}\n${formatDays(w.daysOfWeek)}`}
                >
                  {/* Inline label for wider segments */}
                  {widthPercent > 12 && !compact && (
                    <span className="absolute inset-0 flex items-center justify-center text-[10px] font-medium text-white truncate px-1">
                      {formatTime(w.startHour, w.startMinute)} –{' '}
                      {formatTime(w.endHour, w.endMinute)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Shared tick axis — left spacer matches the row gutter so ticks align with tracks */}
        <div className="flex items-start gap-2">
          <span className="w-16 shrink-0" aria-hidden />
          <div className="relative flex-1 h-4">
            {HOUR_TICKS.map((hour) => {
              const leftPercent = (hour / 24) * 100;
              return (
                <div
                  key={hour}
                  className="absolute flex flex-col items-center"
                  style={{
                    left: `${leftPercent}%`,
                    transform: 'translateX(-50%)',
                  }}
                >
                  <div className="w-px h-1.5 bg-slate-300 dark:bg-slate-600" />
                  <span className="text-[9px] text-slate-400 dark:text-slate-500 leading-none mt-0.5">
                    {formatHourLabel(hour)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legend */}
      {!compact && (
        <div className="flex items-center gap-3 text-[10px] text-slate-500 dark:text-slate-400">
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-sm bg-blue-500/70 dark:bg-blue-400/50" />
            <span>Marketing</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-sm bg-emerald-500/70 dark:bg-emerald-400/50" />
            <span>Informational</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-sm bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700" />
            <span>Outside hours</span>
          </div>
        </div>
      )}
    </div>
  );
}
